import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf as arrayType,
  extend,
  params,
  number as numberType,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  customType,
  useDataModel,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import {
  Alert,
  RadioGroup, Radio, Button,
  SelectionControlLabel,
  DrawerFooter, DrawerBody, Typography, TextField
} from '@one-thd/sui-atomic-components';

import { AddToListProductInfo } from './AddToListProductInfo';
import { BulkProductsSummary } from './BulkProductsSummary';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import { SCREENS, DEFAULT_GROUP_NAME, TRACK } from '../../AddToConstants';
import { triggerAnalytics, cleanIntent } from '../../AddToHelpers';
import { useListMetrics } from '../../hooks/useListMetrics';

export const SelectListGroup = ({
  listId,
  storeId,
  isSharedList,
  products,
  identifiers,
  primaryImage,
  handleClose,
  isNewlyCreated = false,
  newListName,
  isItemBundle,
  bundledProducts
}) => {
  const {
    screen,
    setScreen,
    setSelectedGroup,
    setShowSuccessMessage,
    setSelectedListId,
    setSelectedListName,
    sourceScreen
  } = useContext(AddToCoreContext);
  const { LIST_SUCCESS } = SCREENS;
  const [selectedGroupId, setSelectedGroupId] = useState(DEFAULT_GROUP_NAME);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false);
  const [groupErrorMessage, setGroupErrorMessage] = useState('');

  // Fetch list details to get groups (skip for newly created lists since they're empty)
  const {
    data, loading, refetch, error
  } = useDataModel('listProductDetails', {
    variables: {
      id: listId,
      storeId,
      sharedList: isSharedList
    },
    ssr: true,
    fetchPolicy: 'cache-and-network',
    skip: !listId || isNewlyCreated,
    context: {
      withAuth: true
    }
  });

  const listDetails = data?.listProductDetails;
  // For newly created lists, we know they have no groups yet
  const groups = isNewlyCreated
    ? []
    : (listDetails?.groups || []);

  let listDataForMetrics = null;
  if (isNewlyCreated) {
    listDataForMetrics = {};
  } else if (listDetails) {
    listDataForMetrics = { ...listDetails, id: listId, storeId };
  }

  const { myListInformation } = useListMetrics(products, listDataForMetrics);

  // Set the selected group context on component mount
  useEffect(() => {
    setSelectedGroup(DEFAULT_GROUP_NAME);
  }, [setSelectedGroup]);

  // Set the list context when list details are loaded or for newly created lists
  useEffect(() => {
    if (isNewlyCreated && listId && newListName) {
      // For newly created lists, use the props directly
      setSelectedListId(listId);
      setSelectedListName(newListName);
    } else if (listDetails?.id && listDetails?.name) {
      // For existing lists, use the GraphQL response
      setSelectedListId(listDetails.id);
      setSelectedListName(listDetails.name);
    }
  }, [isNewlyCreated, listId, newListName, listDetails?.id, listDetails?.name, setSelectedListId, setSelectedListName]);

  // Set up mutation for adding items to list group
  const [addItemsToGroup, {
    data: mutationData,
    loading: mutationLoading,
    error: mutationError
  }] = useLazyDataModel('listOperations', {
    context: {
      withAuth: true
    },
    fetchPolicy: 'no-cache'
  });

  // Set up mutation for creating new groups
  const [createGroupMutation, {
    data: createGroupData,
    loading: createGroupLoading,
    error: createGroupError
  }] = useLazyDataModel('listOperations', {
    context: {
      withAuth: true
    },
    fetchPolicy: 'no-cache'
  });

  // Filter out empty groups if needed, similar to SelectGroup pattern
  const availableGroups = groups.filter((group) => group.groupName && group.groupName.trim() !== '');

  const addItemsToList = async ({ groupId = selectedGroupId }) => {
    if (!products || products.length === 0) return;

    // Handle "Default Group" selection - set groupId to null
    const effectiveGroupId = groupId === DEFAULT_GROUP_NAME ? null : groupId;

    // Find the target group to calculate item order
    const targetGroup = groups.find((group) => group.groupId === effectiveGroupId);
    const existingItemsCount = targetGroup?.items?.length || 0;
    const itemsToAdd = isItemBundle ? bundledProducts : products;
    try {
      await addItemsToGroup({
        variables: {
          operationName: 'AddItems',
          id: listId,
          groups: [
            {
              groupId: effectiveGroupId,
              products: itemsToAdd.map((product, index) => {
                const productInput = {
                  quantity: product.quantity || 1,
                  itemOrder: existingItemsCount + index + 1
                };
                // Include itemId for catalog items, intent for intent-only items
                if (product.itemId) {
                  productInput.itemId = product.itemId;
                }
                if (product.intent) {
                  productInput.intent = cleanIntent(product.intent);
                }
                // PRO41-1848 (repair round 2): do NOT forward the SOURCE item's intentId on
                // copy — a copy creates a brand-new item, and the destination backend
                // (mylist-api-gql) hard-rejects a foreign/unrecognized intentId as invalid
                // (MYLIST_ERR_156). intentResolved alone carries the resolution state.
                if (product.intentResolved !== undefined) {
                  productInput.intentResolved = product.intentResolved;
                }
                return productInput;
              })
            }
          ]
        }
      });
    } catch (err) {
      // Error handling for debugging purposes
      // eslint-disable-next-line no-console
      console.error('Error adding items to list group:', err);
    }
  };

  const createNewGroupWithItems = async () => {
    if (!products || products.length === 0 || !newGroupName.trim()) return;

    // Calculate the next group order
    const maxGroupOrder = groups.length > 0
      ? Math.max(...groups.map((group) => group.groupOrder || 0))
      : 0;
    const nextGroupOrder = maxGroupOrder + 1;
    const itemsToAdd = isItemBundle ? bundledProducts : products;

    try {
      await createGroupMutation({
        variables: {
          operationName: 'Create',
          id: listId,
          groups: [
            {
              groupName: newGroupName.trim(),
              groupOrder: nextGroupOrder.toString(),
              products: itemsToAdd.map((product, index) => {
                const productInput = {
                  quantity: product.quantity || 1,
                  itemOrder: index + 1
                };
                // Include itemId for catalog items, intent for intent-only items
                if (product.itemId) {
                  productInput.itemId = product.itemId;
                }
                if (product.intent) {
                  productInput.intent = cleanIntent(product.intent);
                }
                // PRO41-1848 (repair round 2): do NOT forward the SOURCE item's intentId on
                // copy — a copy creates a brand-new item, and the destination backend
                // (mylist-api-gql) hard-rejects a foreign/unrecognized intentId as invalid
                // (MYLIST_ERR_156). intentResolved alone carries the resolution state.
                if (product.intentResolved !== undefined) {
                  productInput.intentResolved = product.intentResolved;
                }
                return productInput;
              })
            }
          ]
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating group with items:', err);
      setGroupErrorMessage('Error creating group. Please try again.');
    }
  };

  const onSelectGroup = (event) => {
    const groupId = event.target.value;
    // Receiving the radio element component as value [object Object] converted to string
    if (groupId.indexOf('object') === -1) {
      setSelectedGroupId(groupId);

      // Check if this is the "create new group" option
      if (groupId === 'create-new-group') {
        setIsCreatingNewGroup(true);
        setSelectedGroup('Create New Group');
      } else {
        setIsCreatingNewGroup(false);
        setGroupErrorMessage('');

        if (groupId === DEFAULT_GROUP_NAME) {
          setSelectedGroup(DEFAULT_GROUP_NAME);
        } else {
          // Find the group name for context
          const selectedGroupData = availableGroups.find((group) => group.groupId === groupId);
          if (selectedGroupData) {
            setSelectedGroup(selectedGroupData.groupName);
          }
        }
      }
    }
  };

  const handleOnChange = (event) => {
    setNewGroupName(event.target.value);
    setGroupErrorMessage('');
  };

  // Helper function to trigger analytics for successful list addition
  const triggerListAddAnalytics = () => {
    if (!products || products.length === 0) return;

    // Pass sourceScreen as location - the analytics listener will handle the mapping
    const location = isSharedList ? 'lists: shared list' : sourceScreen;

    // Format items for analytics
    const items = products.map((product) => ({
      quantity: product.quantity || 1,
      price: product?.price || 'N/A',
      sku: product.itemId,
      aiQuantityEstimate: product?.aiQuantityEstimate ? 'yes' : 'no'
    }));

    // 6.13.1 Add to List action
    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'list add',
      location,
      items,
      isBulkAction: products.length > 1,
      listTotal: myListInformation.listTotal || 0,
      uniqueSkuCount: myListInformation.uniqueSkuCount || 0,
      quantityTotal: myListInformation.quantityTotal || 0
    });
  };

  const handleSubmit = async () => {
    if (isCreatingNewGroup) {
      if (!newGroupName.trim()) {
        setGroupErrorMessage('Please enter a group name');
        return;
      }

      // Check if group name already exists
      const groupExists = availableGroups.some((group) => {
        return group.groupName.toLowerCase() === newGroupName.trim().toLowerCase();
      });

      if (groupExists) {
        setGroupErrorMessage('Group name already exists');
        return;
      }

      await createNewGroupWithItems();
    } else if (selectedGroupId) {
      await addItemsToList({ groupId: selectedGroupId });
    }
  };

  useEffect(() => {
    if (mutationData?.listOperations?.id) {
      // Trigger analytics for successful add to existing group
      triggerListAddAnalytics();
      setShowSuccessMessage(true);
      setScreen(LIST_SUCCESS);
      refetch();
      // Don't close the drawer - keep it open to show success message
      // Adds to an existing group
    }
  }, [mutationData, setShowSuccessMessage, setScreen, refetch, LIST_SUCCESS]);

  useEffect(() => {
    if (createGroupData?.listOperations?.id) {
      // Trigger analytics for successful create group and add items
      triggerListAddAnalytics();
      setShowSuccessMessage(true);
      setScreen(LIST_SUCCESS);
      refetch();
      // Don't close the drawer - keep it open to show success message
      // Creates a group then adds to it
    }
  }, [createGroupData, setShowSuccessMessage, setScreen, refetch, LIST_SUCCESS]);

  const onClose = (isAdded = false) => {
    setSelectedGroupId('');
    setSelectedGroup('');
    setNewGroupName('');
    setIsCreatingNewGroup(false);
    setGroupErrorMessage('');
    if (handleClose) {
      handleClose(isAdded, { isUserInitiated: true });
    }
  };

  if ((loading && !isNewlyCreated) || (!listId && !isNewlyCreated)) {
    return (
      <DrawerBody>
        <Typography>Loading groups...</Typography>
      </DrawerBody>
    );
  }

  if (error) {
    return (
      <DrawerBody>
        <Alert status="error">
          Something went wrong while loading your groups. Refresh to try again.
        </Alert>
      </DrawerBody>
    );
  }

  return (
    <>
      <DrawerBody overflow="scroll">
        {products?.length <= 1 ? (
          <AddToListProductInfo
            identifiers={identifiers}
            primaryImage={primaryImage}
            intent={products[0]?.intent}
          />
        ) : (
          <BulkProductsSummary products={products} />
        )}
        <>
          <div className="sui-pl-5">
            <div className="sui-px-8 sui-py-3">
              <Typography color="primary" variant="h4">
                Add to a Group
              </Typography>
            </div>

            <RadioGroup
              aria-labelledby="group-selection-radio-buttons-group"
              name="radio-buttons-group"
              onChange={onSelectGroup}
              value={selectedGroupId}
            >
              <SelectionControlLabel
                key={DEFAULT_GROUP_NAME}
                value={DEFAULT_GROUP_NAME}
                label={DEFAULT_GROUP_NAME}
              >
                <Radio />
              </SelectionControlLabel>
              {availableGroups.map((group) => (
                <SelectionControlLabel
                  key={group.groupId}
                  value={group.groupId}
                  label={group.groupName}
                >
                  <Radio />
                </SelectionControlLabel>
              ))}
              <SelectionControlLabel
                key="create-new-group"
                value="create-new-group"
                label={(

                  <TextField
                    placeholder="Enter Group Name"
                    value={newGroupName}
                    onChange={(event) => { handleOnChange(event); }}
                    onClick={(event) => event.stopPropagation()}
                    status={groupErrorMessage ? 'error' : null}
                    statusMessage={groupErrorMessage}
                    fullWidth
                    disabled={!isCreatingNewGroup}
                    data-testid="new-group-name-input"
                  />
                )}
              >
                <Radio
                  name="radio-create-new-group"
                  checked={isCreatingNewGroup === true}
                />
              </SelectionControlLabel>
            </RadioGroup>
          </div>
        </>
      </DrawerBody>

      <DrawerFooter position="center" data-testid="add-to-list-group-footer">
        <div className="sui-flex sui-flex-col sui-gap-1">
          <Button
            data-testid="add-to-list-group-add-button"
            variant="primary"
            fullWidth
            disabled={
              !selectedGroupId
              || mutationLoading
              || createGroupLoading
              || (isCreatingNewGroup && !newGroupName.trim())
            }
            onClick={handleSubmit}
            type="submit"
          >
            {(() => {
              if (mutationLoading || createGroupLoading) {
                return isCreatingNewGroup ? 'Creating Group...' : 'Adding...';
              }
              return isCreatingNewGroup ? 'Create Group' : 'Add';
            })()}
          </Button>
          <Button
            data-testid="add-to-list-group-cancel-button"
            variant="secondary"
            fullWidth
            onClick={() => onClose(false)}
            type="button"
            disabled={mutationLoading || createGroupLoading}
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

SelectListGroup.propTypes = {
  listId: PropTypes.string.isRequired,
  storeId: PropTypes.string,
  isSharedList: PropTypes.bool,
  newListName: PropTypes.string,
  isNewlyCreated: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number,
    skuNumber: PropTypes.string,
    intent: PropTypes.string
  })),
  identifiers: PropTypes.shape({
    brandName: PropTypes.string,
    productLabel: PropTypes.string
  }),
  primaryImage: PropTypes.shape({
    url: PropTypes.string
  }),
  handleClose: PropTypes.func,
  isItemBundle: PropTypes.bool,
  bundledProducts: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  }))
};

SelectListGroup.defaultProps = {
  storeId: null,
  isSharedList: false,
  newListName: null,
  isNewlyCreated: false,
  products: [],
  identifiers: {},
  primaryImage: {},
  handleClose: () => { },
  isItemBundle: false,
  bundledProducts: []
};

SelectListGroup.dataModel = extend(useListMetrics.dataModel, {
  listProductDetails: params({
    id: stringType().isRequired(),
    storeId: stringType(),
    sharedList: boolType()
  }).shape({
    id: stringType(),
    itemCount: numberType(),
    itemIds: arrayType(stringType()),
    name: stringType(),
    listAccessType: stringType(),
    subTotal: numberType({ float: true }),
    url: stringType(),
    groups: arrayType(shapeType({
      groupId: stringType(),
      groupName: stringType(),
      groupOrder: numberType(),
      items: arrayType(shapeType({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        intent: stringType(),
        intentId: stringType(),
        intentResolved: boolType(),
        aiQuantity: numberType(),
        calcBase: stringType()
      }))
    }))
  }),
  listOperations: params({
    operationName: customType('Operation').enum(['AddItems', 'Create'], 'AddItems'),
    id: stringType().isRequired(),
    groups: arrayType(customType('GroupInput').shape({
      groupId: stringType(),
      groupName: stringType(),
      groupOrder: stringType(),
      products: arrayType(customType('ProductInput').shape({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        intent: stringType(),
        intentId: stringType(),
        intentResolved: boolType()
      }))
    })),
    name: stringType(),
    listAccessType: customType('AccessTypes').enum(['Shared', 'Private'])
  }).mutation().shape({
    id: stringType(),
    groups: arrayType(customType('Group').shape({
      groupId: stringType(),
      groupName: stringType(),
      groupOrder: stringType(),
      status: stringType(),
      items: arrayType(customType('Item').shape({
        itemId: stringType(),
        quantity: numberType(),
        itemOrder: numberType(),
        comments: stringType(),
        status: stringType(),
        intent: stringType(),
        intentId: stringType(),
        intentResolved: boolType()
      }))
    }))
  })
});
